import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { signOut } from 'firebase/auth';
import { jsPDF } from 'jspdf';
import { auth } from '../firebase';
import { checkSubscription } from '../subscriptionUtils';
import logo from '../assets/logo.png';
import './AppContent.css';
import './CustomsChat.css';
import { ClipLoader } from 'react-spinners';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getBackendUrl } from '../apiConfig';
import {
    HiOutlinePaperAirplane,
    HiOutlinePaperClip,
    HiOutlineDocumentDownload,
    HiOutlinePlusCircle,
  } from 'react-icons/hi';

const loadImageAsBase64 = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        // JPEG has no alpha — fill white so rounded logo corners aren't black
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        resolve({
            dataURL: canvas.toDataURL('image/jpeg', 0.92),
            width: img.naturalWidth,
            height: img.naturalHeight,
        });
    };
    img.onerror = reject;
    img.src = src;
});

const TOPICS = [
    { value: 'general', label: 'General inquiry', prompt: 'Answer as an Australian customs and quarantine expert. Provide accurate, concise information.' },
    { value: 'prohibited_imports', label: 'Prohibited and restricted imports', prompt: 'You are an Australian customs expert on the Customs (Prohibited Imports) Regulations 1956. Answer using PIregulations.json and PIschedules.json primarily, plus your own knowledge when relevant. Be direct and concise. Do not discuss tariff classification, customs duty, TCOs, tariff Schedule 4 concessions, or GST unless the user explicitly asks. Do not mention whether reference excerpts were provided or incomplete.' },
    { value: 'biosecurity', label: 'Biosecurity and quarantine', prompt: 'Explain Australian biosecurity and quarantine requirements under the Biosecurity Act 2015 in relation to the question. Provide accurate, concise information.' },
    { value: 'gst_exemptions', label: 'GST exemptions', prompt: 'You are an Australian GST expert on import GST exemptions. Answer using gst_exemptions.json codes and descriptions only when relevant. Be direct and concise; do not discuss tariff classification, customs duty, TCOs, Schedule 4, or valuation unless the user explicitly asks.' },
    { value: 'Schedule_4', label: 'Schedule 4 concessions', prompt: 'You are an Australian customs expert on Schedule 4 tariff concessions. Answer using Schedule 4 items, treatment codes, reference numbers, and rates only when relevant. Be direct and concise; do not discuss HS classification, valuation, TCOs, or GST unless the user explicitly asks.' },
    { value: 'valuation', label: 'Customs valuation & Interpretive rules', prompt: 'You are an Australian customs expert. Answer using valuation principles and tariff interpretive rules only when relevant to the question. Be direct and concise; avoid long digressions (e.g. do not list every valuation method unless asked).' },
];

const CustomsChat = ({ user }) => {
    const [topicKey, setTopicKey] = useState('general');
    const [query, setQuery] = useState('');
    const [conversation, setConversation] = useState([]);
    const [extractedData, setExtractedData] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isProUser, setIsProUser] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const [classificationsUsed, setClassificationsUsed] = useState(null);
    const [classificationLimit, setClassificationLimit] = useState(null);
    const navigate = useNavigate();
    const backendUrl = getBackendUrl();
    const threadRef = useRef(null);

    useEffect(() => {

        const verifySubscription = async () => {
            const { isAuthenticated, hasActiveSubscription, planLevel } = await checkSubscription();
            if (!isAuthenticated) {
                navigate('/login');
                return;
            }
            if (!hasActiveSubscription) {
                navigate('/subscription');
                return;
            }
            setIsProUser(planLevel === 'Pro');
        };
        verifySubscription();

        const saved = JSON.parse(localStorage.getItem('customspro_chat') || '{}');
        if (Array.isArray(saved.conversations)) {
            setConversation(saved.conversations);
        }
    }, [navigate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [conversation, loading]);

    const persistConversation = (messages) => {
        localStorage.setItem('customspro_chat', JSON.stringify({
            conversations: messages,
            expiry: Date.now() + 7 * 24 * 60 * 60 * 1000,
        }));
    };

    const onDrop = useCallback(async (acceptedFiles) => {
        if (!acceptedFiles.length) return;
        setLoading(true);
        const formData = new FormData();
        acceptedFiles.forEach((file) => formData.append('files', file));
        formData.append('description', query || 'Chat attachment');

        try {
            const response = await fetch(`${backendUrl}/upload`, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Upload failed');
            }
            const data = await response.json();
            setExtractedData((prev) => [...prev, ...(data.extractedData || [])]);
            setUploadedFiles((prev) => [...prev, ...acceptedFiles.map((f) => f.name)]);
            toast.success('File processed - content will be included with your next question.');
        } catch (err) {
            toast.error(err.message || 'Failed to upload file');
        } finally {
            setLoading(false);
        }
    }, [backendUrl, query]);

    const { getRootProps, getInputProps, open: openFilePicker } = useDropzone({
        onDrop,
        accept: {
          'application/pdf': ['.pdf'],
          'text/plain': ['.txt'],
          'image/png': ['.png'],
          'image/jpeg': ['.jpg', '.jpeg'],
        },
        disabled: loading,
        noClick: true,   // we trigger via button
        noKeyboard: true,
    });

    const handleSubmitQuery = async () => {
        if (!query.trim()) {
            toast.error('Please enter a question.');
            return;
        }

        const userId = auth.currentUser?.uid;
        if (!userId) {
            toast.error('Please log in again.');
            navigate('/login');
            return;
        }

        const selectedTopic = TOPICS.find((t) => t.value === topicKey) || TOPICS[0];
        setLoading(true);

        const history = conversation.flatMap((msg) => [
            { role: 'user', content: msg.userQuery },
            { role: 'assistant', content: msg.response },
        ]);

        try {
            const response = await fetch(`${backendUrl}/discuss`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    query: query.trim(),
                    topic: selectedTopic.prompt,
                    topicKey,
                    extractedData,
                    history,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to get a response');
            }

            const newMessage = {
                id: Date.now(),
                userQuery: query.trim(),
                response: data.response,
                topic: selectedTopic.label,
                sourcesUsed: data.sourcesUsed || [],
                timestamp: new Date().toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' }),
            };

            const updated = [...conversation, newMessage];
            setConversation(updated);
            persistConversation(updated);
            setQuery('');
            setExtractedData([]);
            setUploadedFiles([]);

            if (typeof data.classificationsUsed === 'number') {
                setClassificationsUsed(data.classificationsUsed);
            }
            if (typeof data.classificationLimit === 'number') {
                setClassificationLimit(data.classificationLimit);
            }
        } catch (error) {
            console.error('Chat error:', error);
            toast.error(error.message || 'Failed to get response');
        } finally {
            setLoading(false);
        }
    };

    const handleStartNewChat = () => {
        if (conversation.length === 0 && !uploadedFiles.length && !extractedData.length) {
            toast.info('Already on a new chat');
            return;
        }
        const ok = window.confirm('Start a new chat? The current conversation will be cleared from this screen.');
        if (!ok) return;
        setConversation([]);
        setExtractedData([]);
        setUploadedFiles([]);
        setQuery('');
        localStorage.removeItem('customspro_chat');
        toast.info('New chat started');
    };

    const handleSaveChatPdf = async () => {
        if (conversation.length === 0) {
            toast.error('Nothing to save - ask a question first.');
            return;
        }

        try {
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
            });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 15;
            const maxWidth = pageWidth - 2 * margin;
            let yPos = margin;

            const setBodyStyle = () => {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
            };

            const checkPageOverflow = (requiredHeight) => {
                if (yPos + requiredHeight > pageHeight - margin) {
                    doc.addPage();
                    yPos = margin;
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(14);
                    doc.setTextColor(0, 123, 255);
                    doc.text('Customs Pro AI Chat (Continued)', margin, yPos);
                    yPos += 8;
                    doc.setLineWidth(0.5);
                    doc.setDrawColor(0, 123, 255);
                    doc.line(margin, yPos, pageWidth - margin, yPos);
                    yPos += 8;
                    setBodyStyle();
                }
            };

            const addWrappedText = (text, { bold = false, color = [0, 0, 0], fontSize = 10, gapAfter = 3 } = {}) => {
                doc.setFont('helvetica', bold ? 'bold' : 'normal');
                doc.setFontSize(fontSize);
                doc.setTextColor(...color);
                const lines = doc.splitTextToSize(String(text || ''), maxWidth);
                lines.forEach((line) => {
                    checkPageOverflow(5);
                    doc.text(line, margin, yPos);
                    yPos += 5;
                });
                yPos += gapAfter;
                setBodyStyle();
            };

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(16);
            doc.setTextColor(0, 123, 255);
            doc.text('Customs Pro AI - Customs Chat', margin, yPos);
            yPos += 10;

            let calculatedHeight = 0;
            try {
                const { dataURL: logoBase64, width: logoWidth, height: logoHeight } = await loadImageAsBase64(logo);
                const desiredWidth = 30;
                const aspectRatio = logoWidth / logoHeight;
                calculatedHeight = desiredWidth / aspectRatio;
                doc.addImage(logoBase64, 'JPEG', pageWidth - margin - desiredWidth, margin - 5, desiredWidth, calculatedHeight);
            } catch (error) {
                console.error('Logo load failed:', error);
            }

            const logoBottom = (margin - 5) + calculatedHeight;
            yPos = Math.max(yPos, logoBottom + 5);
            doc.setLineWidth(0.5);
            doc.setDrawColor(0, 123, 255);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 8;

            const exportedAt = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
            addWrappedText(`Exported: ${exportedAt}`, { fontSize: 9, color: [80, 80, 80], gapAfter: 2 });
            addWrappedText(`${conversation.length} message${conversation.length === 1 ? '' : 's'}`, {
                fontSize: 9,
                color: [80, 80, 80],
                gapAfter: 6,
            });

            conversation.forEach((msg, index) => {
                checkPageOverflow(20);
                addWrappedText(`Turn ${index + 1} - ${msg.timestamp || ''}`, {
                    bold: true,
                    fontSize: 11,
                    color: [0, 123, 255],
                    gapAfter: 2,
                });
                addWrappedText(`Topic: ${msg.topic || 'General inquiry'}`, {
                    fontSize: 9,
                    color: [80, 80, 80],
                    gapAfter: 3,
                });
                addWrappedText(`You: ${msg.userQuery}`, { bold: true, gapAfter: 2 });
                addWrappedText(`Assistant: ${msg.response}`, { gapAfter: 2 });
                if (msg.sourcesUsed?.length) {
                    addWrappedText(`Sources: ${msg.sourcesUsed.join(', ')}`, {
                        fontSize: 8,
                        color: [100, 100, 100],
                        gapAfter: 2,
                    });
                }
                yPos += 4;
                checkPageOverflow(4);
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.2);
                doc.line(margin, yPos, pageWidth - margin, yPos);
                yPos += 6;
            });

            const stamp = new Date().toISOString().slice(0, 10);
            doc.save(`CustomsPro-Chat-${stamp}.pdf`);
            toast.success('Chat saved as PDF');
        } catch (error) {
            console.error('Save chat PDF failed:', error);
            toast.error('Failed to save chat as PDF');
        }
    };

    const handleLogout = () => {
        signOut(auth).then(() => navigate('/')).catch(() => {
            toast.error('Logout failed');
        });
    };

    return (
      <div className="chat-page">
        <ToastContainer position="top-right" autoClose={5000} />
        {loading && (
          <div className="loading-overlay">
            <ClipLoader color="#007bff" size={50} aria-label="Loading" />
            <span className="loading-text">Processing...</span>
        </div>
    )}

    {/* ===== NAV (unchanged pattern) ===== */}
    <header className="app-header">
        <img src={logo} alt="Customs Pro AI Logo" className="app-logo" />
        <h1>Customs Pro AI</h1>
        <nav className="app-nav">
        <Link to="/" className="nav-item">Home</Link>
        <Link to="/app" className="nav-item">Classify Goods</Link>
        <span className="nav-item active">Customs Chat</span>
        <div
            className={`nav-item pro-dropdown ${!isProUser ? 'disabled' : ''} ${isProDropdownOpen ? 'open' : ''}`}
            title={!isProUser ? 'Pro level users only, please upgrade your subscription' : ''}
            onClick={() => isProUser && setIsProDropdownOpen(!isProDropdownOpen)}
            role="button"
            aria-expanded={isProDropdownOpen}
            aria-controls="pro-dropdown-menu"
        >
            <span>Pro</span>
            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
            <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
            <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
            <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
            <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
            <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
            <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
            <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
            <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
            <Link to="/quarantine-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
            <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
            </div>
        </div>
        <Link to="/settings" className="nav-item">Settings</Link>
        <span className="nav-item logout" onClick={handleLogout} role="button" tabIndex={0} aria-label="Log out">
            Logout
        </span>
        </nav>
    </header>

    {/* ===== CHAT ===== */}
    <main className="chat-main">
        <div className="chat-thread" ref={threadRef}>
        {conversation.length === 0 ? (
            <div className="chat-empty">
            <strong>Customs Chat</strong>
            Ask about prohibited imports, biosecurity, GST exemptions, Schedule 4, valuation, and more.
            Pick a topic below, then type your question.
            </div>
        ) : (
            conversation.map((msg) => (
            <React.Fragment key={msg.id}>
                <div className="chat-turn user">
                <div className="chat-meta">You · {msg.topic} · {msg.timestamp}</div>
                <div className="chat-bubble">{msg.userQuery}</div>
                </div>
                <div className="chat-turn assistant">
                <div className="chat-meta">Assistant</div>
                <div className="chat-bubble">{msg.response}</div>
                {msg.sourcesUsed?.length > 0 && (
                    <div className="chat-sources">Sources: {msg.sourcesUsed.join(', ')}</div>
                )}
                </div>
            </React.Fragment>
            ))
        )}
        </div>

        <div className="chat-composer" {...getRootProps()}>
        <input {...getInputProps()} className="drop-zone-hidden" />

        <div className="composer-top">
            <select
            className="composer-topic"
            value={topicKey}
            onChange={(e) => setTopicKey(e.target.value)}
            aria-label="Chat topic"
            >
            {TOPICS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
            ))}
            </select>
            {classificationsUsed != null && classificationLimit != null && (
            <span className="usage-chip">
                {classificationsUsed} / {classificationLimit} this month
            </span>
            )}
        </div>

        <div className="composer-input-row">
            <textarea
            className="composer-textarea"
            placeholder="Ask a customs or quarantine question…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmitQuery();
                }
            }}
            rows={2}
            />
            <button
            type="button"
            className="composer-send"
            onClick={handleSubmitQuery}
            disabled={loading || !user || !query.trim()}
            aria-label="Send"
            >
            <HiOutlinePaperAirplane size={22} />
            </button>
        </div>

        <div className="composer-actions">
            <button
            type="button"
            className={`composer-btn ${uploadedFiles.length ? 'attach-active' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                openFilePicker();
            }}
            disabled={loading}
            >
            <HiOutlinePaperClip size={16} />
            {uploadedFiles.length ? `${uploadedFiles.length} file(s)` : 'Attach'}
            </button>
            <button
            type="button"
            className="composer-btn"
            onClick={handleSaveChatPdf}
            disabled={loading || conversation.length === 0}
            >
            <HiOutlineDocumentDownload size={16} />
            Save PDF
            </button>
            <button
            type="button"
            className="composer-btn"
            onClick={handleStartNewChat}
            disabled={loading}
            >
            <HiOutlinePlusCircle size={16} />
            New chat
            </button>
        </div>
        </div>
    </main>
    </div>
);
};

export default CustomsChat;  